/**
 * CheckoutSummaryDialog — compact scrollable confirmation list.
 *
 * Shows selected items in a table-like layout with column headers,
 * optional Remove button per row, and Back/Continue footer.
 * Column config uses the same { name, mnemonic, ui_class } shape as DataTable
 * for future migration compatibility.
 *
 * Props:
 *   open             — boolean: controls dialog visibility
 *   onClose          — () => void: called on X button click
 *   title            — string: gold title (e.g. "Checkout", "Confirm Destruction")
 *   items            — array: data objects to display
 *   columns          — array: [{ name, mnemonic, ui_class }] — which fields to show
 *   onRemove         — optional (item) => void: if provided, shows Remove button per row
 *   onBack           — optional () => void: left button
 *   onContinue       — () => void: right button (primary action)
 *   backLabel        — optional string (default "Back")
 *   continueLabel    — optional string (default "Continue")
 *   continueDisabled — optional boolean
 */
import {Dialog, DialogContent, Grid, Typography} from "@mui/material";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import {getFormattedProperty} from "../../utils/utils";
import {StyledButtonContained, StyledButtonOutlined} from "../../utils/uiUtils";

export const CheckoutSummaryDialog = ({
                                          open,
                                          onClose,
                                          title = "Checkout",
                                          items = [],
                                          columns = [],
                                          onRemove,
                                          onBack,
                                          onContinue,
                                          backLabel = "Back",
                                          continueLabel = "Continue",
                                          continueDisabled = false,
                                      }) => {

    const hasRemove = typeof onRemove === 'function';

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="sm"
            PaperProps={{sx: {borderRadius: "12px", backgroundColor: "#3A3E3F"}}}
        >
            <DialogContent sx={{p: 3, display: "flex", flexDirection: "column", maxHeight: "80vh"}}>
                {/* Header */}
                <Grid container justifyContent="space-between" alignItems="flex-start" sx={{flexShrink: 0}}>
                    <Grid item>
                        <Typography sx={{color: "#FFC540", fontWeight: 600, fontSize: "18px", fontFamily: "Poppins, sans-serif"}}>
                            {title}
                        </Typography>
                        <Typography sx={{color: "rgba(255,255,255,0.7)", fontSize: "13px", fontFamily: "Poppins, sans-serif", mt: 0.5}}>
                            <b>{items.length}</b> item{items.length !== 1 ? "s" : ""}
                        </Typography>
                    </Grid>
                    <Grid item>
                        <CloseIcon
                            onClick={onClose}
                            sx={{color: "#FFFFFF", cursor: "pointer", fontSize: "20px"}}
                        />
                    </Grid>
                </Grid>

                {/* Column headers */}
                <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    mt: 2,
                    pb: 1,
                    borderBottom: "1px solid #5F5F5F",
                    flexShrink: 0,
                }}>
                    {columns.map((col, idx) => (
                        <Typography
                            key={`header-${idx}`}
                            sx={{
                                flex: 1,
                                color: "rgba(255,255,255,0.5)",
                                fontSize: "11px",
                                fontWeight: 600,
                                fontFamily: "Poppins, sans-serif",
                                textTransform: "uppercase",
                            }}
                        >
                            {col.name}
                        </Typography>
                    ))}
                    {hasRemove && (
                        <Box sx={{width: 80, flexShrink: 0}}/>
                    )}
                </Box>

                {/* Scrollable rows */}
                <Box sx={{flexGrow: 1, overflowY: "auto", overflowX: "hidden", maxHeight: "300px", mt: 0.5, pr: 2}}>
                    {items.length === 0 ? (
                        <Typography sx={{color: "rgba(255,255,255,0.5)", fontSize: "13px", fontFamily: "Poppins, sans-serif", py: 3, textAlign: "center"}}>
                            No items selected.
                        </Typography>
                    ) : (
                        items.map((item, rowIdx) => (
                            <Box
                                key={`row-${rowIdx}-${item.item_id || rowIdx}`}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    py: 0.75,
                                    borderBottom: "1px solid rgba(95,95,95,0.4)",
                                    "&:last-child": {borderBottom: "none"},
                                }}
                            >
                                {columns.map((col, colIdx) => (
                                    <Typography
                                        key={`cell-${rowIdx}-${colIdx}`}
                                        sx={{
                                            flex: 1,
                                            color: "#FFFFFF",
                                            fontSize: "13px",
                                            fontFamily: "Poppins, sans-serif",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            pr: 1,
                                        }}
                                    >
                                        {getFormattedProperty(item, col.mnemonic, col.ui_class)}
                                    </Typography>
                                ))}
                                {hasRemove && (
                                    <Box sx={{width: 80, flexShrink: 0, textAlign: "right"}}>
                                        <StyledButtonOutlined
                                            variant="outlined"
                                            onClick={() => onRemove(item)}
                                            sx={{
                                                minWidth: "unset",
                                                width: "70px",
                                                height: "28px",
                                                fontSize: "11px",
                                                color: "#FFC540",
                                                borderColor: "#FFC540",
                                                "&:hover": {
                                                    backgroundColor: "#FFC540",
                                                    color: "#3A3E3F",
                                                    borderColor: "#FFC540",
                                                },
                                            }}
                                        >
                                            Remove
                                        </StyledButtonOutlined>
                                    </Box>
                                )}
                            </Box>
                        ))
                    )}
                </Box>

                {/* Footer */}
                <Grid container justifyContent="space-between" alignItems="center" sx={{mt: 2, flexShrink: 0}}>
                    <Grid item>
                        {onBack && (
                            <StyledButtonOutlined
                                variant="outlined"
                                onClick={onBack}
                                sx={{width: "160px", height: "50px"}}
                            >
                                {backLabel}
                            </StyledButtonOutlined>
                        )}
                    </Grid>
                    <Grid item>
                        <StyledButtonContained
                            variant="contained"
                            onClick={() => onContinue?.(items)}
                            disabled={continueDisabled}
                            sx={{width: "160px", height: "50px", borderRadius: "30px"}}
                        >
                            {continueLabel}
                        </StyledButtonContained>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
};
